import { DepotsRecus } from '../espace-deposant/DepotsRecus.js';
import { AccesDeposant } from '../espace-deposant/AccesDeposant.js';
import {
  FORMATS_PROFIL,
  formatDate,
  formatPrice,
  profilDeclareSchema,
  type ProfilDeclare,
  type ReponseProfil,
} from '@artosera/shared';
import { Tabs, Button, Checkbox, Input, PriceInput, Textarea } from '@artosera/ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import {
  profilsApi,
  useGesteProfil,
  useOeuvresProfil,
  useProfilContact,
  useScoreProfil,
  useVocabulaireProfils,
} from '../../api/profils.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { RetourGeste } from '../RetourGeste.js';
import { NomArtiste, SelectionArtistes } from './SelectionArtistes.js';
import { RaisonsCorrespondance } from './RaisonsCorrespondance.js';
import styles from './Profils.module.css';

export function GoutsContact({ contactId }: { contactId: string }) {
  const { galerie } = useShellContext();
  if (
    !galerieAModule(galerie, 'profils-collectionneurs') ||
    !galerieALaction(galerie, 'profils:read')
  )
    return null;
  return <Gouts contactId={contactId} />;
}

function Gouts({ contactId }: { contactId: string }) {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const profil = useProfilContact(galerieSlug, contactId);
  const score = useScoreProfil(galerieSlug, contactId);
  const oeuvres = useOeuvresProfil(galerieSlug, contactId);
  const recalcul = useGesteProfil(galerieSlug, (_corps: void, appel) =>
    profilsApi.recalculerScore(galerieSlug, contactId, appel),
  );
  const [retour, setRetour] = useState<string | null>(null);
  const peutEcrire = galerieALaction(galerie, 'profils:write');
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  return (
    <div className={styles.contenu}>
      <section className={styles.section}>
        <h2>{t('profils.score')}</h2>
        {score.isLoading && <p role="status">{t('commun:chargement')}</p>}
        {score.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
        {score.data && (
          <>
            {score.data.exclu ? (
              <p>{t(`profils.exclusionsScore.${score.data.cause}`)}</p>
            ) : (
              <>
                <p>
                  {score.data.score === null
                    ? t('profils.scoreAbsent')
                    : `${score.data.score} / 100`}
                </p>
                {score.data.score !== null && (
                  <meter
                    className={styles.jauge}
                    min={0}
                    max={100}
                    value={score.data.score}
                    aria-label={t('profils.score')}
                  />
                )}
                <p>
                  {score.data.tendance === null
                    ? t('profils.tendanceAbsente')
                    : t('profils.tendance', {
                        variation: `${score.data.tendance >= 0 ? '+' : ''}${score.data.tendance}`,
                      })}
                </p>
                {score.data.calculeLe && (
                  <p>
                    {t('profils.calculeLe', {
                      date: formatDate(new Date(score.data.calculeLe), langue, 'court'),
                    })}
                  </p>
                )}
                <ul>
                  {score.data.lignes.map((ligne) => (
                    <li key={`${ligne.signal}-${ligne.objetId}`}>
                      {t(`profils.signaux.${ligne.signal}`)} · {ligne.libelle} ·{' '}
                      {t('profils.ligneScore', {
                        jours: Math.floor(ligne.ageJours),
                        points: ligne.points,
                      })}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
        {peutEcrire && (
          <Button
            variant="secondary"
            loading={recalcul.isPending}
            onClick={() =>
              recalcul.mutate(undefined, {
                onSuccess: () => setRetour(t('profils.scoreRecalcule')),
              })
            }
          >
            {t('profils.recalculer')}
          </Button>
        )}
        {recalcul.isError && (
          <p role="alert">
            {estApiError(recalcul.error) ? recalcul.error.message : t('commun:erreurReseau')}
          </p>
        )}
        <RetourGeste message={retour} />
      </section>
      {profil.isLoading && <p role="status">{t('commun:chargement')}</p>}
      {profil.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {profil.data && (
        <div className={styles.profils}>
          <ProfilDeclareVue
            key={`${contactId}-${profil.data.budgetMasque}`}
            contactId={contactId}
            donnees={profil.data}
            peutEcrire={peutEcrire}
          />
          <section className={styles.section}>
            <h2>{t('profils.observe')}</h2>
            <dl className={styles.donnees}>
              <div>
                <dt>{t('profils.artistesObserves')}</dt>
                <dd>
                  {profil.data.observe.artistes.length
                    ? profil.data.observe.artistes.map((artiste) => (
                        <p key={artiste.artisteId}>
                          <NomArtiste galerieSlug={galerieSlug} id={artiste.artisteId} /> ·{' '}
                          {artiste.nombre}
                          {artiste.formatDominant &&
                            ` · ${t(`profils.formats.${artiste.formatDominant}`)}`}
                        </p>
                      ))
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>{t('profils.techniques')}</dt>
                <dd>
                  {profil.data.observe.techniques
                    .map((ligne) => `${ligne.technique} (${ligne.nombre})`)
                    .join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt>{t('profils.formatsTitre')}</dt>
                <dd>
                  {profil.data.observe.formats
                    .map((ligne) => `${t(`profils.formats.${ligne.format}`)} (${ligne.nombre})`)
                    .join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt>{t('profils.typesTitre')}</dt>
                <dd>
                  {profil.data.observe.typesOeuvre
                    .map((ligne) => `${t(`profils.types.${ligne.type}`)} (${ligne.nombre})`)
                    .join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt>{t('profils.budget')}</dt>
                <dd>
                  {profil.data.budgetMasque
                    ? t('profils.budgetMasque')
                    : t('profils.fourchette', {
                        min:
                          profil.data.observe.budgetMinCentimes === null
                            ? '—'
                            : formatPrice(profil.data.observe.budgetMinCentimes, 'EUR', langue),
                        max:
                          profil.data.observe.budgetMaxCentimes === null
                            ? '—'
                            : formatPrice(profil.data.observe.budgetMaxCentimes, 'EUR', langue),
                      })}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      )}
      <section className={styles.section}>
        <h2>{t('profils.oeuvresCorrespondantes')}</h2>
        {oeuvres.isLoading && <p role="status">{t('commun:chargement')}</p>}
        {oeuvres.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
        {oeuvres.data?.oeuvres.length === 0 && <p>{t('profils.aucuneOeuvre')}</p>}
        <ul>
          {oeuvres.data?.oeuvres.map((oeuvre) => (
            <li key={oeuvre.oeuvreId}>
              <Link to={`/${galerieSlug}/oeuvres/${oeuvre.oeuvreId}`}>{oeuvre.titre}</Link>
              <RaisonsCorrespondance galerieSlug={galerieSlug} raisons={oeuvre.raisons} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ProfilDeclareVue({
  contactId,
  donnees,
  peutEcrire,
}: {
  contactId: string;
  donnees: ReponseProfil;
  peutEcrire: boolean;
}) {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const [profil, setProfil] = useState(donnees.declare);
  const [techniques, setTechniques] = useState(profil.techniques.join(', '));
  const [erreur, setErreur] = useState<string | null>(null);
  const [retour, setRetour] = useState<string | null>(null);
  const vocabulaire = useVocabulaireProfils(galerieSlug);
  const modifier = useGesteProfil(galerieSlug, (corps: ProfilDeclare, appel) =>
    profilsApi.modifierProfil(galerieSlug, contactId, corps, appel),
  );
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  function poser<K extends keyof ProfilDeclare>(cle: K, valeur: ProfilDeclare[K]) {
    setProfil((precedent) => ({ ...precedent, [cle]: valeur }));
    setRetour(null);
  }
  async function enregistrer() {
    setErreur(null);
    setRetour(null);
    const validation = profilDeclareSchema.safeParse({
      ...profil,
      techniques: techniques
        .split(',')
        .map((texte) => texte.trim())
        .filter(Boolean),
    });
    if (!validation.success) {
      setErreur(t('profils.profilInvalide'));
      return;
    }
    try {
      await modifier.mutateAsync(validation.data);
      setRetour(t('profils.enregistre'));
    } catch (cause) {
      setErreur(estApiError(cause) ? cause.message : t('commun:erreurReseau'));
    }
  }
  if (!peutEcrire)
    return (
      <section className={styles.section}>
        <h2>{t('profils.declare')}</h2>
        <dl className={styles.donnees}>
          <div>
            <dt>{t('profils.artistes')}</dt>
            <dd>
              {donnees.declare.artistesRecherches.length
                ? donnees.declare.artistesRecherches.map((id) => (
                    <p key={id}>
                      <NomArtiste galerieSlug={galerieSlug} id={id} />
                    </p>
                  ))
                : '—'}
            </dd>
          </div>
          <div>
            <dt>{t('profils.techniques')}</dt>
            <dd>{donnees.declare.techniques.join(', ') || '—'}</dd>
          </div>
          <div>
            <dt>{t('profils.formatsTitre')}</dt>
            <dd>
              {donnees.declare.formats.map((format) => t(`profils.formats.${format}`)).join(', ') ||
                '—'}
            </dd>
          </div>
          <div>
            <dt>{t('profils.typesTitre')}</dt>
            <dd>
              {donnees.declare.typesOeuvre.map((type) => t(`profils.types.${type}`)).join(', ') ||
                '—'}
            </dd>
          </div>
          <div>
            <dt>{t('profils.budget')}</dt>
            <dd>
              {donnees.budgetMasque
                ? t('profils.budgetMasque')
                : t('profils.fourchette', {
                    min:
                      donnees.declare.budgetMinCentimes === null
                        ? '—'
                        : formatPrice(donnees.declare.budgetMinCentimes, 'EUR', langue),
                    max:
                      donnees.declare.budgetMaxCentimes === null
                        ? '—'
                        : formatPrice(donnees.declare.budgetMaxCentimes, 'EUR', langue),
                  })}
            </dd>
          </div>
          <div>
            <dt>{t('profils.themes')}</dt>
            <dd>{donnees.declare.themes.join(', ') || '—'}</dd>
          </div>
          <div>
            <dt>{t('profils.artistesExclus')}</dt>
            <dd>
              {donnees.declare.exclusions.length
                ? donnees.declare.exclusions.map((id) => (
                    <p key={id}>
                      <NomArtiste galerieSlug={galerieSlug} id={id} />
                    </p>
                  ))
                : '—'}
            </dd>
          </div>
          <div>
            <dt>{t('profils.nePasSolliciter')}</dt>
            <dd>{t(donnees.declare.nePasSolliciter ? 'profils.oui' : 'profils.non')}</dd>
          </div>
          <div>
            <dt>{t('profils.notes')}</dt>
            <dd>{donnees.declare.notes || '—'}</dd>
          </div>
        </dl>
      </section>
    );
  return (
    <section className={styles.section}>
      <h2>{t('profils.declare')}</h2>
      <form
        className={styles.formulaire}
        onSubmit={(event) => {
          event.preventDefault();
          void enregistrer();
        }}
      >
        <SelectionArtistes
          galerieSlug={galerieSlug}
          id="profil-artistes"
          label={t('profils.artistes')}
          valeur={profil.artistesRecherches}
          onChange={(ids) => poser('artistesRecherches', ids)}
        />
        <Field htmlFor="profil-techniques" label={t('profils.techniques')}>
          <Input
            id="profil-techniques"
            value={techniques}
            list="profil-techniques-suggestions"
            onChange={(event) => {
              setTechniques(event.target.value);
              setRetour(null);
            }}
          />
          <datalist id="profil-techniques-suggestions">
            {vocabulaire.data?.techniques.map((technique) => (
              <option key={technique} value={technique} />
            ))}
          </datalist>
          <p>{t('profils.techniquesAide')}</p>
        </Field>
        <fieldset>
          <legend>{t('profils.formatsTitre')}</legend>
          <div className={styles.cases}>
            {FORMATS_PROFIL.map((format) => (
              <Checkbox
                key={format}
                id={`profil-${format}`}
                label={t(`profils.formats.${format}`)}
                checked={profil.formats.includes(format)}
                onCheckedChange={(coche) =>
                  poser(
                    'formats',
                    coche === true
                      ? [...profil.formats, format]
                      : profil.formats.filter((valeur) => valeur !== format),
                  )
                }
              />
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>{t('profils.typesTitre')}</legend>
          <div className={styles.cases}>
            {(['unique', 'edition'] as const).map((type) => (
              <Checkbox
                key={type}
                id={`profil-${type}`}
                label={t(`profils.types.${type}`)}
                checked={profil.typesOeuvre.includes(type)}
                onCheckedChange={(coche) =>
                  poser(
                    'typesOeuvre',
                    coche === true
                      ? [...profil.typesOeuvre, type]
                      : profil.typesOeuvre.filter((valeur) => valeur !== type),
                  )
                }
              />
            ))}
          </div>
        </fieldset>
        {donnees.budgetMasque ? (
          <p>
            {t('profils.budget')} · {t('profils.budgetMasque')}
          </p>
        ) : (
          <>
            <Field htmlFor="profil-budget-min" label={t('profils.budgetMin')}>
              <PriceInput
                id="profil-budget-min"
                optional
                cents={profil.budgetMinCentimes}
                onCentsChange={(valeur) => poser('budgetMinCentimes', valeur)}
                lang={langue}
                currency="EUR"
              />
            </Field>
            <Field htmlFor="profil-budget-max" label={t('profils.budgetMax')}>
              <PriceInput
                id="profil-budget-max"
                optional
                cents={profil.budgetMaxCentimes}
                onCentsChange={(valeur) => poser('budgetMaxCentimes', valeur)}
                lang={langue}
                currency="EUR"
              />
            </Field>
          </>
        )}
        <Field htmlFor="profil-themes" label={t('profils.themes')}>
          <select
            id="profil-themes"
            multiple
            className={styles.selection}
            value={profil.themes}
            onChange={(event) =>
              poser(
                'themes',
                Array.from(event.target.selectedOptions, (option) => option.value),
              )
            }
          >
            {[...new Set([...profil.themes, ...(vocabulaire.data?.themes ?? [])])].map((theme) => (
              <option key={theme} value={theme}>
                {theme}
              </option>
            ))}
          </select>
        </Field>
        {vocabulaire.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
        <SelectionArtistes
          galerieSlug={galerieSlug}
          id="profil-exclusions"
          label={t('profils.artistesExclus')}
          valeur={profil.exclusions}
          onChange={(ids) => poser('exclusions', ids)}
        />
        <Checkbox
          id="profil-ne-pas-solliciter"
          label={t('profils.nePasSolliciter')}
          checked={profil.nePasSolliciter}
          onCheckedChange={(valeur) => poser('nePasSolliciter', valeur === true)}
        />
        <Field htmlFor="profil-notes" label={t('profils.notes')}>
          <Textarea
            id="profil-notes"
            maxLength={4000}
            value={profil.notes}
            onChange={(event) => poser('notes', event.target.value)}
          />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
        <RetourGeste message={retour} />
        <Button type="submit" loading={modifier.isPending}>
          {t('commun:actions.enregistrer')}
        </Button>
      </form>
    </section>
  );
}

export function OngletsContact({
  contactId,
  emails,
  children,
}: {
  contactId: string;
  emails: readonly string[];
  children: ReactNode;
}) {
  const { t } = useTranslation('regie');
  const { galerie } = useShellContext();
  const [params] = useSearchParams();
  const profils =
    galerieAModule(galerie, 'profils-collectionneurs') && galerieALaction(galerie, 'profils:read');
  const deposant =
    galerieAModule(galerie, 'espace-deposant') && galerieALaction(galerie, 'movements:read');
  if (!profils && !deposant) return <>{children}</>;
  const onglets = [{ value: 'identite', label: t('contacts.onglets.identite'), content: children }];
  if (profils)
    onglets.push({
      value: 'gouts',
      label: t('profils.gouts'),
      content: <GoutsContact contactId={contactId} />,
    });
  if (deposant)
    onglets.push({
      value: 'oeuvres-confiees',
      label: t('deposant.oeuvres'),
      content: (
        <>
          <DepotsRecus contactId={contactId} />
          <AccesDeposant contactId={contactId} emails={emails} />
        </>
      ),
    });
  const demande = params.get('onglet');
  return (
    <Tabs
      defaultValue={onglets.find((o) => o.value === demande)?.value ?? 'identite'}
      tabs={onglets}
    />
  );
}
